import { ContentProfile } from "../../../api/admin";

export type ProfileStatus = {
  label: string;
  color: "default" | "info" | "success" | "warning" | "error";
  /** Ready profiles can't be edited, change devices, or change their gift. */
  locked: boolean;
  /** Devices the last submission missed; Sync / Retry push retries them. */
  failedDevices?: number;
};

export const isReady = (profile: Pick<ContentProfile, "readyAt">) =>
  !!profile.readyAt;

export const profileStatus = (profile: ContentProfile): ProfileStatus => {
  if (!isReady(profile)) {
    return { label: "Draft (ignored)", color: "default", locked: false };
  }
  if (!profile.submittedAt) {
    if (!profile.orderName) {
      // Synced before submittedAt existed: only per-device push times, from
      // possibly different Syncs, so there is no failure count to show.
      const times = Object.values(profile.pushedAt ?? {}).map((time) =>
        new Date(time).getTime(),
      );
      return times.length
        ? {
            label: `Ready — last synced ${new Date(Math.max(...times)).toLocaleString()}`,
            color: "info",
            locked: true,
          }
        : { label: "Ready — not synced yet", color: "info", locked: true };
    }
    // The order trigger fills the devices, then pushes; devices without a
    // submission mean that push failed.
    return profile.clockIds.length
      ? { label: "Ready — push failed", color: "error", locked: true }
      : {
          label: "Ready — waiting for the order's clocks",
          color: "info",
          locked: true,
        };
  }
  const submittedAt = new Date(profile.submittedAt).getTime();
  const missed = profile.clockIds.filter((clockId) => {
    const pushedAt = profile.pushedAt[clockId];
    return !pushedAt || new Date(pushedAt).getTime() < submittedAt;
  }).length;
  return missed
    ? {
        label: `Submitted with ${missed} failed device${missed === 1 ? "" : "s"}`,
        color: "warning",
        locked: true,
        failedDevices: missed,
      }
    : {
        label: `Submitted ${new Date(submittedAt).toLocaleString()}`,
        color: "success",
        locked: true,
      };
};
